// The Region and Time Zone dropdowns on the studio profile, built from Intl.
//
// Not in domain/: this is a list of choices the browser already knows, and
// what gets stored is a plain locale identifier and an IANA time-zone name.
// domain/locale.js derives currency and date formats from the stored locale.
//
// ── Derived at run time, not shipped as a generated list ──────────────────
//
// MMS's Region dropdown has CLDR's full list of `Country - Language` entries
// (1602, including "United Kingdom - Welsh"). Intl cannot enumerate locales —
// `Intl.supportedValuesOf` does not cover them — so this derives a smaller
// list from what the runtime exposes. Every entry is a well-formed BCP-47
// locale the runtime can format with, by construction, and nothing goes
// stale when CLDR changes.
//
// The cost is the minority-language rows: this yields roughly 390 entries —
// every country with its primary language, plus every language with its
// default country — so a studio teaching in Welsh in the UK cannot pick
// `cy-GB` from the list.

const EN = ['en'];

// ISO 3166-1 alpha-2 is exactly two uppercase letters, and ISO 639-1 exactly
// two lowercase ones, so both spaces are small enough to enumerate. A code the
// runtime does not know is echoed back unchanged by DisplayNames (`fallback:
// 'code'`), which is the filter.
function enumerateCodes(type, alphabet) {
  const names = new Intl.DisplayNames(EN, { type, fallback: 'code' });
  const out = [];
  for (const a of alphabet) {
    for (const b of alphabet) {
      const code = a + b;
      let label;
      try { label = names.of(code); } catch { continue; }
      if (label && label !== code) out.push({ code, label });
    }
  }
  return out;
}

const UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const LOWER = 'abcdefghijklmnopqrstuvwxyz'.split('');

let regionCache = null;

// [{ id: 'en-US', label: 'United States - English' }, ...]
//
// Labelled `Country - Language` in that order, which is MMS's, and sorted by
// the label so the dropdown reads alphabetically by country.
export function regionOptions() {
  if (regionCache) return regionCache;

  const regions = enumerateCodes('region', UPPER);
  const languages = enumerateCodes('language', LOWER);
  const regionLabel = new Map(regions.map((r) => [r.code, r.label]));
  const languageLabel = new Map(languages.map((l) => [l.code, l.label]));

  // `maximize()` is the runtime's CLDR likely-subtags table: 'und-KR' becomes
  // 'ko-Kore-KR' and 'pt' becomes 'pt-Latn-BR'. Taking both directions gives
  // every country its own language and every language its own country, so a
  // studio teaching Portuguese in Portugal and one in Brazil both have a row.
  const byId = new Map();
  const add = (language, region) => {
    if (!language || !region) return;
    if (!regionLabel.has(region) || !languageLabel.has(language)) return;
    const id = `${language}-${region}`;
    if (byId.has(id)) return;
    byId.set(id, { id, label: `${regionLabel.get(region)} - ${languageLabel.get(language)}` });
  };

  for (const { code } of regions) {
    try { add(new Intl.Locale(`und-${code}`).maximize().language, code); } catch { /* skipped */ }
  }
  for (const { code } of languages) {
    try { add(code, new Intl.Locale(code).maximize().region); } catch { /* skipped */ }
  }

  // en-IN falls through both likely-subtag directions above — India's likely
  // language is Hindi, and English's likely region is the United States — yet
  // MMS offers "India - English" and writes its dates `dd-MM-y` (#551,
  // `MMS_SHORT_DATES` in `core/src/locale.rs`), which only a studio that can
  // pick this Region reaches (#602).
  add('en', 'IN');

  regionCache = [...byId.values()].sort((a, b) => a.label.localeCompare(b.label));
  return regionCache;
}

// The label for a stored locale, for the profile card. A locale that is not in
// the derived list — one stored earlier, or typed by hand — is shown as itself
// rather than as nothing, because blank reads as "unset".
//
// Spelled out as `Country - Language` when the runtime can name both halves,
// and as the bare id only when it cannot. A studio's first Region is the
// browser's (#462), and a browser may say `fr-CA` — a real locale this list
// has no row for — so "Canada - French" is an ordinary value to show.
export function regionLabel(locale) {
  if (!locale) return null;
  const match = regionOptions().find((o) => o.id === locale);
  return match ? match.label : (spelledOut(locale) || locale);
}

function spelledOut(locale) {
  try {
    const tag = new Intl.Locale(String(locale));
    if (!tag.region) return null;
    const region = new Intl.DisplayNames(EN, { type: 'region', fallback: 'none' }).of(tag.region);
    const language = new Intl.DisplayNames(EN, { type: 'language', fallback: 'none' }).of(tag.language);
    return region && language ? `${region} - ${language}` : null;
  } catch {
    return null;
  }
}

// What this browser says its reader reads, most preferred first, for
// `signupLocale` in src/domain/locale.js (#462). `navigator.languages` where
// there is one, `navigator.language` where there is only that, and nothing
// rather than a throw where there is no navigator at all.
export function browserLanguages() {
  try {
    const nav = globalThis.navigator;
    if (nav?.languages?.length) return [...nav.languages];
    return nav?.language ? [nav.language] : [];
  } catch {
    return [];
  }
}

let zoneCache = null;

// [{ id: 'America/Los_Angeles', label: '(UTC-08:00) America/Los_Angeles' }, ...]
//
// MMS labels these Windows-style — "(UTC-08:00) Pacific Time (US & Canada)".
// Those names are Microsoft's, not IANA's, and no runtime table maps between
// them, so the offset is computed from the zone and the IANA name is shown as
// itself.
//
// The offset is the zone's offset now, so a DST zone reads differently in
// January and July; what is stored is the zone, which carries its own rules.
export function timeZoneOptions() {
  if (zoneCache) return zoneCache;
  const zones = typeof Intl.supportedValuesOf === 'function'
    ? Intl.supportedValuesOf('timeZone')
    // Older runtimes have no enumeration. One entry — the browser's own zone —
    // beats an empty dropdown.
    : [browserTimeZone()].filter(Boolean);

  zoneCache = zones
    .map((id) => ({ id, label: `(${offsetOf(id)}) ${id}`, sort: offsetMinutes(id) }))
    .sort((a, b) => a.sort - b.sort || a.id.localeCompare(b.id))
    .map(({ id, label }) => ({ id, label }));
  return zoneCache;
}

function offsetOf(timeZone) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longOffset' })
      .formatToParts(new Date());
    const name = parts.find((p) => p.type === 'timeZoneName')?.value || '';
    // 'GMT' with no offset is UTC itself; 'GMT-08:00' is what everything else
    // gives back.
    return name === 'GMT' ? 'UTC+00:00' : name.replace('GMT', 'UTC');
  } catch {
    return 'UTC+00:00';
  }
}

function offsetMinutes(timeZone) {
  const match = /([+-])(\d{2}):(\d{2})/.exec(offsetOf(timeZone));
  if (!match) return 0;
  const sign = match[1] === '-' ? -1 : 1;
  return sign * (Number(match[2]) * 60 + Number(match[3]));
}

// The zone this machine is in. domain/studio.js has no default time zone: the
// right one is a property of the machine, not of the record, and a default in
// the domain would normalise the same document differently in a test, a Cloud
// Function and a browser.
export function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

// Whether this studio has a time zone of its own, as opposed to one inferred
// from whatever machine is reading the record (#245).
//
// The stored value is what the reminder worker reads, so it decides when mail
// goes out; the profile card and Accounts & Invoices both ask this, and the
// card marks an inferred zone (`timeZoneFact`). Anything that needs a zone to
// compute with calls `displayTimeZone` — CalendarPage and StudentDetailPage do
// — because an inferred zone is the right answer there.
export function hasStoredTimeZone(studio) {
  return Boolean(studio?.timeZone);
}

// What the profile card and the drawer show for a studio that has not chosen
// one: this machine's zone, not a blank.
export function displayTimeZone(studio) {
  return studio?.timeZone || browserTimeZone();
}

// The profile card's Time Zone row: the value, and whether it is the studio's
// own or this device's.
//
// Returned as a pair so the card decides how to mark it. `zone` is null only
// when the machine will not answer either; the card renders '—' for that.
export function timeZoneFact(studio) {
  return { zone: displayTimeZone(studio), stored: hasStoredTimeZone(studio) };
}
